/**
 * Ordering Element Grader
 */

import size from 'lodash/size';
import { createScoringEngine } from '@ai-content-hub/content-core';
import type { ScoringConfig } from '@ai-content-hub/content-core';
import type {
    ElementGrader,
    GradingConfig,
    GradingResult,
    ValidationResult,
    ValidationError,
} from '@ai-content-hub/content-kit';
import type { OrderingElementData, OrderingAnswer } from './schema';
import { getCorrectOrder, MIN_ITEMS } from './schema';

export const orderingGrader: ElementGrader<OrderingElementData, OrderingAnswer> = {
    grade(data: OrderingElementData, answer: OrderingAnswer, config: GradingConfig = {}): GradingResult {
        const { items } = data;

        if (!items || items.length === 0) {
            return {
                isCorrect: false,
                score: 0,
                max: 0,
                feedback: 'No items to order.',
            };
        }

        const correctOrder = getCorrectOrder(data);

        const scoringConfig: ScoringConfig = {
            max: config.max ?? items.length,
            partialCreditEnabled: config.partialCredit ?? true,
        };

        const engine = createScoringEngine(scoringConfig);
        // Host-forced submit can carry a short order. gradeOrder returns 0 on any length
        // mismatch, so score short arrays by position here (no fake IDs padded in).
        const given: string[] = Array.isArray(answer) ? answer : [];
        let result;
        if (given.length < correctOrder.length) {
            let hits = 0;
            for (let i = 0; i < given.length; i++) {
                if (given[i] === correctOrder[i]) hits++;
            }
            const sr = engine.calculatePartialScore(hits, correctOrder.length);
            result = {
                isCorrect: sr.isCorrect,
                score: sr.score,
                max: sr.max,
                correctAnswer: correctOrder,
                details: { correctPositions: hits, totalPositions: correctOrder.length },
            };
        } else {
            result = engine.gradeOrder(given, correctOrder);
        }
        const correctPositions = (result.details?.correctPositions as number) ?? 0;

        return {
            ...result,
            feedback: result.isCorrect
                ? 'Correct! All items are in the right order.'
                : `You got ${correctPositions} of ${items.length} items in the correct position.`,
            details: {
                ...result.details,
                totalItems: items.length,
                correctOrder,
            },
        };
    },

    getCorrectAnswer(data: OrderingElementData): OrderingAnswer {
        return getCorrectOrder(data);
    },

    validate(data: OrderingElementData): ValidationResult {
        const errors: ValidationError[] = [];

        if (!data.question || !data.question.trim()) {
            errors.push({
                field: 'question',
                message: 'Question is required',
                code: 'required',
            });
        }

        if (!data.items || size(data.items) < MIN_ITEMS) {
            errors.push({
                field: 'items',
                message: 'Phải có ít nhất 02 mục và đã được sắp xếp thứ tự đúng.',
                code: 'minItems',
            });
        } else {
            data.items.forEach((item, index) => {
                if (!item.text || !item.text.trim()) {
                    errors.push({
                        field: `items.${index}.text`,
                        message: `Item ${index + 1} text is required`,
                        code: 'required',
                    });
                }
            });
        }

        return {
            isValid: size(errors) === 0,
            errors,
        };
    },

    /**
     * Each ordering item (position) is 1 gradable question.
     */
    countQuestions(data: OrderingElementData): number {
        return (data.items ?? []).length;
    },
};

export default orderingGrader;
